<template>
  <div class="contents">
    <section
      v-if="auth.loggedIn"
      :aria-label="$t.account.accountMenu.summary"
      class="b-b b-grey-70 pb-4"
    >
      <div class="mt-5 flex items-center gap-2 px-1">
        <div class="flex items-center gap-2">
          <p class="title-2">
            {{ greeting }}
          </p>
          <p
            v-if="hasLoyaltyAccess && accountMenu?.summary === 'meters-and-credits' && loyaltyInfo?.currentTier"
            class="rounded-xs bg-brand-8 p-1 overline-2 c-white"
          >
            {{ loyaltyInfo.currentTier }}
          </p>
        </div>

        <span class="ml-a h-10 w-10 flex center rounded-full bg-brand-6 p-2 fw-bold <lg:text-sm">
          {{ userInitials }}
        </span>
      </div>

      <dl
        v-if="user.enableLoyaltyFeatures && accountMenu?.summary === 'meters-and-credits'"
        class="grid cols-2 mt-2 gap-2"
      >
        <div class="rounded-lg bg-white p-4 shadow-xs">
          <dt class="flex gap-1 overline-2">
            {{ $t.account.accountMenu.meters }}
            <vf-tooltip
              class-trigger="flex items-center"
              placement="bottom-start"
              @click="DialogHowMetersWorks.open()"
            >
              <template #trigger>
                <vf-icon name="info" />
              </template>
              <p>
                {{ $t.account.accountMenu.availableMetersTooltip }}
              </p>
            </vf-tooltip>
          </dt>
          <dd class="mt-1 title-2">
            {{ meterProgress.current }}<span
              v-if="meterProgress.max"
              class="overline-2"
            >/{{ meterProgress.max }}</span>
          </dd>
        </div>
        <div class="rounded-lg bg-white p-4 shadow-xs">
          <dt class="flex gap-1 overline-2">
            {{ $t.account.accountMenu.storeCredits }}
            <vf-tooltip
              class-trigger="flex items-center"
              placement="bottom-start"
              @click="openCreditHowItWorks"
            >
              <template #trigger>
                <vf-icon name="info" />
              </template>
              <p>
                {{ $t.account.accountMenu.availableCreditsTooltip }}
              </p>
            </vf-tooltip>
          </dt>
          <dd class="mt-1 title-2">
            {{ credits }}
          </dd>
        </div>
      </dl>

      <section
        v-else-if="user.enableLoyaltyFeatures && accountMenu?.summary === 'points-and-rewards'"
        :aria-label="$t.pointsAndRewards.title"
        class="mt-2 f-col rounded bg-brand-8 p-3 c-white shadow-xs"
      >
        <div class="flex items-center gap-3">
          <vf-icon size="2xl" :src="loyaltyLogo" />
          <div class="min-w-0 grow">
            <dl class="grid cols-2">
              <div class="b-r b-white pr-6">
                <dt class="overline-2">
                  {{ $t.account.accountMenu.rewardBalance }}
                </dt>
                <dd class="title-1">
                  {{ loyalty.currentReward }}
                </dd>
              </div>
              <div class="pl-6">
                <dt class="overline-2">
                  {{ $t.pointsAndRewards.pointsEarned }}
                </dt>
                <dd class="title-1">
                  {{ getLocalizedNumber(user.loyaltyPointsBalance) }}
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <p class="mt-4 caption-1 op-70">
          {{ $t.account.accountMenu.applyRewardAtCheckout }}
        </p>
      </section>
    </section>

    <nav :aria-label="$t.myAccount" :class="{ 'mt-4': auth.loggedIn }">
      <ul class="fw-bold lg:mt-4 space-y-1">
        <loyalty-nav-item v-if="!auth.loggedIn && $feature.enableLoyaltyFeatures && !user.isWranxOrIpaProfile" />
        <li
          v-if="!auth.loggedIn"
          v-style:display="loyaltyDisplayStyle"
          aria-hidden="true"
          class="b-b b-grey-70 pb-2"
          role="presentation"
        />
        <li
          v-for="({ icon, to, onClick, label, className, action }, index) in (auth.loggedIn ? loggedInLinks : guestLinks)"
          :key="index"
          class="flex items-center gap-2 py-3 subtitle-1"
          :class="className"
        >
          <vf-icon :name="icon" size="lg" />
          <vf-link :to variant="quieter" @click="onClick?.()">
            {{ label }}
          </vf-link>
          <base-button
            v-if="action"
            class="ml-a text-sm underlined"
            @click="handleLogout"
          >
            {{ action.label }}
          </base-button>
        </li>
      </ul>
    </nav>
    <vf-link
      v-if="hasLoyaltyAccess && membershipCardOffer?.url"
      class="mt-4 flex items-center gap-2 b b-grey-70 rounded-lg p-4"
      :to="membershipCardOffer.url"
      variant="quieter"
    >
      <div class="min-w-0 grow">
        <p class="subtitle-1">
          {{ membershipCardOffer.heading }}
        </p>
        <p v-if="membershipCardOffer.subheading" class="mt-2 body-2">
          {{ membershipCardOffer.subheading }}
        </p>
      </div>
      <div class="rounded-md bg-brand-8 p-2">
        <vf-icon class="c-white" size="lg" :src="assets.qrCode" />
      </div>
    </vf-link>
    <vf-link
      v-if="hasLoyaltyAccess && appDownloadOffer?.url"
      class="mt-2 flex items-center gap-2 b b-grey-70 rounded-lg p-4"
      target="_blank"
      :to="appDownloadOffer.url"
      variant="quieter"
    >
      <div class="min-w-0 grow">
        <p class="subtitle-1">
          {{ appDownloadOffer.heading }}
        </p>
        <p v-if="appDownloadOffer.subheading" class="mt-2 body-2">
          {{ appDownloadOffer.subheading }}
        </p>
      </div>
      <div class="rounded-xl bg-white p-2 shadow-lg">
        <vf-icon size="xl" :src="assets.brandLogo" />
      </div>
    </vf-link>
    <div
      v-if="hasLoyaltyAccess && auth.loggedIn && activeOffer"
      aria-hidden="true"
      class="mt-5 b-b b-grey-70"
    />
    <section
      v-if="hasLoyaltyAccess && auth.loggedIn && activeOffer"
      :aria-label="$t.account.accountMenu.activeOffer"
      class="mt-5 flex items-start gap-3 overflow-hidden rounded-lg bg-white p-4 shadow-xs"
    >
      <base-picture
        v-if="activeOffer.imageUrl"
        :alt="activeOffer.name"
        class="h-21 w-21 shrink-0 overflow-hidden rounded"
        fit="cover"
        :src="activeOffer.imageUrl"
      />
      <div class="min-w-0 grow text-left">
        <h2 class="subtitle-1">
          {{ activeOffer.name }}
        </h2>
        <p v-if="activeOffer.description" class="mt-1 body-2">
          {{ activeOffer.description }}
          <base-button
            v-if="activeOffer.details"
            class="underlined"
            @click="DialogInfo.open({ body: activeOffer.details, title: activeOffer.name })"
          >
            {{ $t.account.loyaltyDashboard.details }}
          </base-button>
        </p>
      </div>
    </section>
    <dialog-info />
    <dialog-how-meters-works />
    <dialog-credit-how-it-works />
  </div>
</template>

<script lang="ts" setup>
const emit = defineEmits<{ resolve: [v?: number] }>()

interface LinkItem {
  icon: string
  label: string
  to?: string
  onClick?: () => void
  className?: string
  action?: {
    label: string
  }
}

interface LoyaltyMeterTier {
  minMeters: number
  maxMeters: number
}

const auth = useAuthStore()
const user = useUserStore()
const loyalty = useLoyaltyStore()
const { $feature, $formatPrice, $t } = useNuxtApp()
const route = useRoute()
const { DialogSignIn } = useDialogsStore()
const currencyCode = useCurrencyCode()
const { consumer } = useApi()
const { loyaltyToken, updateLoyaltyToken } = useLoyalty()
const DialogInfo = createModal('info')
const DialogHowMetersWorks = createDialog('how-meters-works')
const DialogCreditHowItWorks = createDialog('credit-how-it-works')
const locale = useLocale()
const loyaltyInfo = user.profile?.loyaltyInfo

const openCreditHowItWorks = () => DialogCreditHowItWorks.open({
  vouchers: loyalty.vouchers?.applicableLoyaltyVouchers || []
})
const mappedLocale = getMappedLocale(locale)
const { pages, assets, components } = useAppConfig()
const { accountMenu, appDownload, membershipCard } = pages.account.loyaltyDashboard ?? {}
const { logo: loyaltyLogo } = components.vf.loyalty
const membershipCardCampaign = membershipCard ? `${membershipCard.campaign}_${mappedLocale}` : ''

const formatMeterValue = (meters: number) => getLocalizedNumber(meters, locale)

const userInitials = computed(() =>
  `${user.details?.firstName?.slice(0, 1).toUpperCase() || ''}${user.details?.lastName?.slice(0, 1).toUpperCase() || ''}`
  || user.details?.email?.charAt(0).toUpperCase()
  || ''
)
const greeting = computed(() =>
  user.details?.firstName
    ? replaceAll($t.account.accountMenu.greetingWithName, { name: user.details.firstName })
    : $t.account.accountMenu.greeting
)
const meterProgress = computed(() => {
  const tier = loyaltyInfo?.loyaltyTiers
    ?.find(({ name }) => name === loyaltyInfo?.currentTier) as LoyaltyMeterTier | undefined
  if (!tier) return { current: '0' }

  if (tier.maxMeters < 0)
    return { current: `+${formatMeterValue(tier.minMeters)}m` }

  const currentMeters = Math.max(0, loyaltyInfo?.loyaltyMeters ?? 0)

  return {
    current: formatMeterValue(currentMeters),
    max: formatMeterValue(tier.maxMeters)
  }
})
const credits = computed(() =>
  $formatPrice(loyalty.availableBalance, currencyCode, { minimumFractionDigits: 0 })
)

const activeOffer = computed(() => user.activeOffers
  .find(({ requiredData }) => !requiredData?.includes('birthday') && !requiredData?.includes('optin')))

const loyaltyDisplayStyle = computed(() =>
  Object.entries($feature.disableLoyaltyNavItemOnRoutes || {}).reduce((acc, [key, patternsToDisable]) => {
    acc[key] = patternsToDisable.some((patternToDisable) =>
      convertPatternToRegex(patternToDisable).test(route.path))
      ? 'none'
      : 'flex'
    return acc
  }, {} as Record<string, string>)
)

const {
  data: accountMenuOffers,
  execute: getAccountMenuOffers
} = consumer.getLoyaltyOffers({
  pagination: false,
  respondable: true,
  show_target: true,
  campaign: membershipCardCampaign,
}, {
  immediate: false,
  key: 'account-menu-loyalty-offers'
})
const membershipCardOffer = computed(() => accountMenuOffers.value?.data.offers
  .find(({ internal_name }) => internal_name === membershipCardCampaign.toLowerCase()))
const appDownloadOffer = computed(() => accountMenuOffers.value?.data.offers
  .find(({ internal_name }) => internal_name === (appDownload ? `${appDownload.offerName}_${mappedLocale}` : '').toLowerCase()))
const hasLoyaltyAccess = computed(() => Boolean(
  auth.loggedIn
  && user.enableLoyaltyFeatures
  && loyaltyToken.value.memberToken
))

const handleLogout = () => {
  emit('resolve')
  auth.logout()
}

const handleSignIn = () => {
  DialogSignIn.open({
    formLocation: 'modal:single:header:none'
  })
}

watch(() => auth.loggedIn, (loggedIn) => {
  if (loggedIn) emit('resolve')
})

watch(
  () => [auth.loggedIn, user.enableLoyaltyFeatures] as const,
  async ([loggedIn, enableLoyaltyFeatures]) => {
    if (!loggedIn || !enableLoyaltyFeatures) return

    await Promise.all([loyalty.getVouchers(), updateLoyaltyToken()])
    if (!loyaltyToken.value.memberToken) return

    if (membershipCardCampaign)
      await getAccountMenuOffers()
  },
  { immediate: true }
)

const guestLinks: LinkItem[] = [
  { icon: 'account', label: $t.signInPanel.signInSignUp, onClick: handleSignIn },
  { icon: 'order', to: '/order-status', label: $t.signInPanel.orderStatus, className: 'py-2' },
  { icon: 'return-process', to: '/returns', label: $t.signInPanel.returns, className: 'py-2' }
]

const loggedInLinks = computed<LinkItem[]>(() => [
  {
    icon: 'account',
    to: '/account',
    label: $t.myAccount,
    action: { label: $t.signOut },
  },
  { icon: 'heart', to: '/favorites', label: $t.favorites },
  { icon: 'order', to: '/account/order-history', label: $t.account.accountMenu.trackOrReturnOrder },
  user.enableLoyaltyFeatures && { icon: 'xplr-pass', to: '/account/loyalty', label: $t.xplrPass }
].flatMap((item) => item ? [item] : []))
</script>
